import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { authChannelsResponseSchema, authRequestCodeSchema, authVerifyCodeSchema, type AuthUser } from '@unified-lk/api-contracts'
import { EventTypes, logEvent } from '@unified-lk/event-log'
import type { AuthChannelAttachAction, AuthChannelOption } from '@unified-lk/auth-ui'
import { buildStaffNavigation } from '@unified-lk/nav'
import { fetchTenantBootstrap, resolveTenantBootstrap, type TenantBootstrapModel } from '@unified-lk/tenant-config'
import { Button } from '@unified-lk/ui'
import { StaffOperationsWorkspace } from './components/StaffOperationsWorkspace'
import { StaffPatientsWorkspace } from './components/patients/StaffPatientsWorkspace'
import { PublicGiftCertificatePage } from './components/public/PublicGiftCertificatePage'
import { PublicMisPlanPage } from './components/public/PublicMisPlanPage'
import { PublicTreatmentPlanPage } from './components/public/PublicTreatmentPlanPage'
import { PublicSharedPlanRouter } from './components/public/PublicSharedPlanRouter'
import { StaffConstructorWorkspace } from './components/plans/StaffConstructorWorkspace'
import { StaffPlansWorkspace } from './components/plans/StaffPlansWorkspace'
import { StaffPaymentLinksWorkspace } from './components/payments/StaffPaymentLinksWorkspace'
import type { AuthSession } from './types/plans'

type AuthStep = 'phone' | 'code'
type NoticeTone = 'info' | 'success' | 'error'
type StaffRouteView =
  | 'dashboard'
  | 'patients'
  | 'payments'
  | 'schedule'
  | 'constructor'
  | 'plans'
  | 'communications'
  | 'gift-certificates'
  | 'diagnocat'
  | 'promotions'
  | 'content'
  | 'analytics'
  | 'users'
  | 'reports'
  | 'documents'
  | 'payment-links'
  | 'media'

const STAFF_SECTION_PATH_MAP: Record<StaffRouteView, string> = {
  dashboard: '/',
  patients: '/patients',
  payments: '/payments',
  schedule: '/schedule',
  constructor: '/constructor',
  plans: '/mis-plans',
  communications: '/communications',
  'gift-certificates': '/gift-certificates',
  diagnocat: '/diagnocat',
  promotions: '/promotions',
  content: '/content',
  analytics: '/analytics',
  users: '/users',
  reports: '/reports',
  // 03.05.26: модуль «Документы» — рассрочка / налоговая справка через MIS proxy.
  documents: '/documents',
  // 11.05.26: ad-hoc платёжные ссылки
  'payment-links': '/payment-links',
  // 20.05.26: медиа-репозиторий
  media: '/media-library',
}

const fallbackAuthChannelOptions: AuthChannelOption[] = [
  { channel: 'sms', displayName: 'SMS', selectable: true },
  { channel: 'whatsapp', displayName: 'WhatsApp', selectable: true },
  { channel: 'tg_bot', displayName: 'Telegram', selectable: true },
  { channel: 'max_bot', displayName: 'MAX', selectable: true },
]

function normalizePhone(value: string) {
  const digits = value.replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  if (digits.length === 10) {
    return `7${digits}`
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    return `7${digits.slice(1)}`
  }

  return digits
}

function formatPhoneLabel(value: string) {
  if (!value) {
    return 'Не указан'
  }

  const digits = normalizePhone(value)
  return digits ? `+${digits}` : value
}

function channelDisplayName(channel: string) {
  switch (channel) {
    case 'sms':
      return 'SMS'
    case 'whatsapp':
      return 'WhatsApp'
    case 'tg_bot':
      return 'Telegram'
    case 'max_bot':
      return 'MAX'
    default:
      return channel
  }
}

function getSpaBasePath(apiBase: string): string {
  return apiBase.replace(/\/api(\/.*)?$/, '')
}

function viewFromPathname(pathname: string, basePath: string): StaffRouteView {
  const relative = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length) || '/'
    : '/'

  let best: StaffRouteView = 'dashboard'
  let bestLen = 0

  for (const [view, suffix] of Object.entries(STAFF_SECTION_PATH_MAP) as Array<[StaffRouteView, string]>) {
    if (suffix === '/') {
      continue
    }

    if (relative === suffix || relative.startsWith(`${suffix}/`)) {
      if (suffix.length > bestLen) {
        best = view
        bestLen = suffix.length
      }
    }
  }

  return best
}

function noticeClassName(tone: NoticeTone) {
  switch (tone) {
    case 'success':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'error':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    default:
      return 'border-sky-200 bg-sky-50 text-sky-700'
  }
}

function resolvePublicToken() {
  const params = new URLSearchParams(window.location.search)
  const queryToken = params.get('publicToken') ?? params.get('secret')
  if (queryToken) {
    return decodeURIComponent(queryToken)
  }

  const match = window.location.pathname.match(/\/(?:d|plans\/public|public\/plans)\/([^/?#]+)/)

  return match ? decodeURIComponent(match[1]) : null
}

function resolvePublicGiftToken() {
  const queryToken = new URLSearchParams(window.location.search).get('giftToken')
  if (queryToken) {
    return decodeURIComponent(queryToken)
  }

  const match = window.location.pathname.match(/\/(?:gift-certificates\/public|public\/gift-certificates)\/([^/?#]+)/)

  return match ? decodeURIComponent(match[1]) : null
}

function resolvePublicMisToken() {
  const queryToken = new URLSearchParams(window.location.search).get('misPlanToken')
  if (queryToken) {
    return decodeURIComponent(queryToken)
  }

  const match = window.location.pathname.match(/\/(?:p\/mis|public\/mis-plans)\/([^/?#]+)/)

  return match ? decodeURIComponent(match[1]) : null
}

export function App() {
  const [bootstrap, setBootstrap] = useState<TenantBootstrapModel>(() => resolveTenantBootstrap('staff'))
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [channel, setChannel] = useState('sms')
  const [authStep, setAuthStep] = useState<AuthStep>('phone')
  const [authChannelOptions, setAuthChannelOptions] = useState<AuthChannelOption[]>([])
  const [authAttachActions, setAuthAttachActions] = useState<AuthChannelAttachAction[]>([])
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [channelsError, setChannelsError] = useState<string | null>(null)
  const [employeeFound, setEmployeeFound] = useState<boolean | null>(null)
  const [employeeDisplayName, setEmployeeDisplayName] = useState<string | null>(null)
  const [requestMessage, setRequestMessage] = useState<string | null>(null)
  const [requestTone, setRequestTone] = useState<NoticeTone>('info')
  const [requestedPhoneLabel, setRequestedPhoneLabel] = useState<string | null>(null)
  const [session, setSession] = useState<AuthSession | null>(() => {
    try {
      // If the URL carries an explicit secret_login link, ignore any stored
      // session so the impersonation can take effect cleanly.
      const params = new URLSearchParams(window.location.search)
      if (params.get('secret_login')) {
        localStorage.removeItem('unified_lk_staff_session')
        return null
      }
      const raw = localStorage.getItem('unified_lk_staff_session')
      return raw ? (JSON.parse(raw) as AuthSession) : null
    } catch {
      return null
    }
  })
  const [sessionVerified, setSessionVerified] = useState<boolean>(() => {
    try {
      return !localStorage.getItem('unified_lk_staff_session')
    } catch {
      return true
    }
  })
  const basePath = useMemo(() => getSpaBasePath(bootstrap.apiBase), [bootstrap.apiBase])
  const [currentView, setCurrentView] = useState<StaffRouteView>(() => viewFromPathname(window.location.pathname, getSpaBasePath(resolveTenantBootstrap('staff').apiBase)))
  const [loading, setLoading] = useState(false)
  const publicToken = useMemo(() => resolvePublicToken(), [])
  const publicGiftToken = useMemo(() => resolvePublicGiftToken(), [])
  const publicMisToken = useMemo(() => resolvePublicMisToken(), [])
  const navigation = useMemo(
    () => buildStaffNavigation(bootstrap, session?.user.permissions ?? undefined),
    [bootstrap, session?.user.permissions],
  )
  const themeStyle = useMemo(
    () => ({ ['--lk-primary' as string]: bootstrap.branding.primaryColor }) as CSSProperties,
    [bootstrap.branding.primaryColor],
  )
  const navigateTo = useCallback((view: string) => {
    const nextView = view as StaffRouteView
    setCurrentView(nextView)
    const suffix = STAFF_SECTION_PATH_MAP[nextView]
    if (!suffix) {
      return
    }

    const nextPath = `${basePath}${suffix === '/' ? '/' : suffix}`
    window.history.pushState({ view: nextView }, '', `${nextPath}${window.location.search}`)
  }, [basePath])

  useEffect(() => {
    void fetchTenantBootstrap('staff').then((payload) => {
      setBootstrap(payload)
    })
  }, [])

  useEffect(() => {
    const onPopState = () => {
      setCurrentView(viewFromPathname(window.location.pathname, basePath))
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [basePath])

  useEffect(() => {
    if (session) {
      try {
        localStorage.setItem('unified_lk_staff_session', JSON.stringify(session))
      } catch {
        // ignore storage errors
      }
    } else {
      localStorage.removeItem('unified_lk_staff_session')
    }
  }, [session])

  useEffect(() => {
    if (!session) {
      setSessionVerified(true)
      return
    }
    // 03.05.26: при каждой загрузке принудительно дёргаем /auth/refresh,
    // даже если access_token ещё валиден. Это нужно потому что JWT кэширует
    // permissions с момента выпуска, а админ-миграции (например, выдача
    // documents/documents_create) меняют права в БД задним числом — без
    // refresh новые пункты меню (например «Документы») не появятся,
    // пока access_token не протухнет сам. refresh_token живёт долго,
    // identity берётся из БД заново → permissions всегда свежие.
    const refreshFromServer = async (): Promise<boolean> => {
      try {
        const refreshResponse = await fetch(bootstrap.auth.refreshPath, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: session.refreshToken }),
        })
        if (!refreshResponse.ok) return false
        const refreshData = (await refreshResponse.json()) as {
          success?: boolean; access_token?: string; refresh_token?: string; csrf_token?: string; user?: AuthUser
        }
        if (!refreshData.success || !refreshData.access_token || !refreshData.refresh_token || !refreshData.user) {
          return false
        }
        setSession({
          accessToken: refreshData.access_token,
          refreshToken: refreshData.refresh_token,
          csrfToken: refreshData.csrf_token ?? '',
          user: refreshData.user,
        })
        return true
      } catch {
        return false
      }
    }

    // Не блокируем UI: показываем ЛК сразу, refresh permissions в фоне.
    setSessionVerified(true)
    void (async () => {
      const refreshed = await refreshFromServer()
      if (refreshed) {
        return
      }
      try {
        const verifyResponse = await fetch(bootstrap.auth.verifyPath, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${session.accessToken}` },
        })
        if (!verifyResponse.ok) {
          setSession(null)
        }
      } catch {
        // network ошибка — оставляем существующую сессию.
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const secretLoginToken = params.get('secret_login')
    if (!secretLoginToken) return

    // Always honour an explicit secret_login link: drop any cached session so the
    // impersonation actually switches to the requested target user instead of
    // silently keeping the operator on their own LK.
    try {
      localStorage.removeItem('unified_lk_staff_session')
    } catch {
      // ignore storage errors
    }
    void fetch(`${bootstrap.apiBase}/auth/secret-login?token=${encodeURIComponent(secretLoginToken)}`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (r) => {
        if (!r.ok) return
        const data = (await r.json()) as { success?: boolean; access_token?: string; refresh_token?: string; csrf_token?: string; user?: AuthUser }
        if (!data.success || !data.access_token || !data.refresh_token || !data.user) return
        setSession({ accessToken: data.access_token, refreshToken: data.refresh_token, csrfToken: data.csrf_token ?? '', user: data.user })
        setSessionVerified(true)
        setCurrentView(viewFromPathname(window.location.pathname, basePath))
        params.delete('secret_login')
        const newSearch = params.toString()
        window.history.replaceState(null, '', window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash)
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, bootstrap.apiBase])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const devToken = params.get('dev')
    if (!devToken || session) return
    void fetch(`${bootstrap.apiBase}/auth/dev-login?secret=${encodeURIComponent(devToken)}`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (r) => {
        if (!r.ok) return
        const data = (await r.json()) as { success?: boolean; access_token?: string; refresh_token?: string; csrf_token?: string; user?: AuthUser }
        if (!data.success || !data.access_token || !data.refresh_token || !data.user) return
        setSession({ accessToken: data.access_token, refreshToken: data.refresh_token, csrfToken: data.csrf_token ?? '', user: data.user })
        setCurrentView(viewFromPathname(window.location.pathname, basePath))
        params.delete('dev')
        const newSearch = params.toString()
        window.history.replaceState(null, '', window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash)
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, bootstrap.apiBase, session])

  useEffect(() => {
    if (session || authStep !== 'phone') {
      return
    }

    const normalizedPhone = normalizePhone(phone)
    if (normalizedPhone.length < 11) {
      setAuthChannelOptions([])
      setAuthAttachActions([])
      setChannelsLoading(false)
      setChannelsError(null)
      setEmployeeFound(null)
      setEmployeeDisplayName(null)
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      setChannelsLoading(true)
      setChannelsError(null)
      void fetch(`${bootstrap.auth.channelsPath}?phone=${encodeURIComponent(normalizedPhone)}`, {
        headers: {
          Accept: 'application/json',
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Channels request failed with ${response.status}`)
          }

          const json = await response.json()
          const parsed = authChannelsResponseSchema.parse(json)
          if (cancelled) {
            return
          }

          setEmployeeFound(typeof parsed.employee_found === 'boolean' ? parsed.employee_found : null)
          setEmployeeDisplayName(parsed.employee_display_name ?? null)

          const nextOptions = (parsed.channel_options ?? []).map((option) => ({
            channel: option.channel,
            displayName: option.display_name,
            selectable: option.selectable,
            blockedReason: option.blocked_reason ?? null,
            attachAction: option.attach_action
              ? {
                  channel: option.attach_action.channel,
                  label: option.attach_action.label,
                  url: option.attach_action.url,
                }
              : null,
          }))

          const resolvedOptions = nextOptions.length ? nextOptions : fallbackAuthChannelOptions
          setAuthChannelOptions(resolvedOptions)
          setAuthAttachActions(
            (parsed.attach_actions ?? []).map((action) => ({
              channel: action.channel,
              label: action.label,
              url: action.url,
            })),
          )

          const stillSelectable = resolvedOptions.some((option) => option.channel === channel && option.selectable !== false)
          if (!stillSelectable) {
            const firstSelectable = resolvedOptions.find((option) => option.selectable !== false)
            if (firstSelectable) {
              setChannel(firstSelectable.channel)
            }
          }
        })
        .catch(() => {
          if (cancelled) {
            return
          }

          setAuthChannelOptions([])
          setAuthAttachActions([])
          setChannelsError('Не удалось проверить доступные каналы. Попробуйте ещё раз.')
          setEmployeeFound(null)
        })
        .finally(() => {
          if (!cancelled) {
            setChannelsLoading(false)
          }
        })
    }, 250)

    const checkTimer = window.setTimeout(() => {
      void fetch(bootstrap.auth.channelsPath.replace('/channels', '/phone-check') + '?phone=' + encodeURIComponent(normalizedPhone), {
        headers: { Accept: 'application/json' },
      })
        .then(async (r) => {
          if (cancelled) return
          const json = await r.json()
          if (typeof json.found === 'boolean') {
            setEmployeeFound(json.found)
          }
          if (typeof json.display_name === 'string' && json.display_name.trim() !== '') {
            setEmployeeDisplayName(json.display_name)
          }
        })
        .catch(() => undefined)
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.clearTimeout(checkTimer)
    }
  }, [authStep, bootstrap.auth.channelsPath, channel, phone, session])

  const hasPhoneForChannels = normalizePhone(phone).length >= 11

  const autoVerifyCodeRef = useRef<string | null>(null)

  useEffect(() => {
    if (code.length !== 6) {
      autoVerifyCodeRef.current = null
    }
  }, [code])

  useEffect(() => {
    if (session || authStep !== 'code' || loading || code.length !== 6) {
      return
    }
    if (autoVerifyCodeRef.current === code) {
      return
    }
    autoVerifyCodeRef.current = code
    void verifyCode()
  }, [authStep, code, loading, session])

  useEffect(() => {
    if (!session) {
      return
    }

    if (!navigation.length) {
      return
    }

    const hasCurrentView = navigation.some((item) => item.key === currentView)
    if (!hasCurrentView) {
      const next = navigation[0].key as StaffRouteView
      setCurrentView(next)
      const suffix = STAFF_SECTION_PATH_MAP[next] ?? '/'
      const nextPath = `${basePath}${suffix === '/' ? '/' : suffix}`
      window.history.replaceState({ view: next }, '', `${nextPath}${window.location.search}`)
    }
  }, [basePath, currentView, navigation, session])

  async function requestCode() {
    const normalizedPhone = normalizePhone(phone)
    if (normalizedPhone.length < 11) {
      setRequestTone('error')
      setRequestMessage('Введите корректный номер телефона.')
      return
    }

    setLoading(true)
    setRequestTone('info')
    setRequestMessage(null)

    try {
      const response = await fetch(bootstrap.auth.requestCodePath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ phone: normalizedPhone, channel }),
      })
      const json = await response.json()
      if (!response.ok) {
        throw new Error(String(json.message ?? json.error ?? 'Не удалось отправить код'))
      }

      const parsed = authRequestCodeSchema.parse(json)
      const helper = parsed.debug_code ? `${parsed.message ?? 'Код отправлен.'} Dev code: ${parsed.debug_code}` : (parsed.message ?? 'Код отправлен.')
      setPhone(normalizedPhone)
      setRequestedPhoneLabel(parsed.phone_masked ?? formatPhoneLabel(normalizedPhone))
      setChannel(parsed.channel ?? channel)
      setAuthStep('code')
      setCode('')
      setRequestTone('success')
      setRequestMessage(helper)
    } catch (error) {
      setRequestTone('error')
      setRequestMessage(error instanceof Error ? error.message : 'Не удалось отправить код')
    } finally {
      setLoading(false)
    }
  }

  async function verifyCode() {
    setLoading(true)

    try {
      const response = await fetch(bootstrap.auth.verifyCodePath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ phone: normalizePhone(phone), code }),
      })
      const json = await response.json()
      if (!response.ok) {
        throw new Error(String(json.message ?? json.error ?? 'Не удалось проверить код'))
      }

      const parsed = authVerifyCodeSchema.parse(json)
      setSession({
        accessToken: parsed.access_token,
        refreshToken: parsed.refresh_token,
        csrfToken: parsed.csrf_token,
        user: parsed.user,
      })
      logEvent(bootstrap.apiBase, parsed.access_token, EventTypes.STAFF_AUTH_LOGIN, { phone: normalizePhone(phone) })
      const initialView = viewFromPathname(window.location.pathname, basePath)
      setCurrentView(initialView)
      setRequestTone('success')
      setRequestMessage(`Авторизация успешна через ${channelDisplayName(parsed.channel ?? channel)}.`)
      setCode('')
    } catch (error) {
      setRequestTone('error')
      setRequestMessage(error instanceof Error ? error.message : 'Не удалось проверить код')
    } finally {
      setLoading(false)
    }
  }

  async function logout() {
    if (session) {
      logEvent(bootstrap.apiBase, session.accessToken, EventTypes.STAFF_AUTH_LOGOUT, {})
    }
    await fetch(bootstrap.auth.logoutPath, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    })

    setSession(null)
    setCurrentView(viewFromPathname(window.location.pathname, basePath))
    setAuthStep('phone')
    setCode('')
    setRequestedPhoneLabel(null)
    setRequestTone('info')
    setRequestMessage('Сессия завершена.')
  }

  if (publicGiftToken) {
    return <PublicGiftCertificatePage bootstrap={bootstrap} publicToken={publicGiftToken} />
  }

  if (publicMisToken) {
    return <PublicMisPlanPage bootstrap={bootstrap} publicToken={publicMisToken} />
  }

  if (publicToken) {
    return <PublicSharedPlanRouter bootstrap={bootstrap} token={publicToken} />
  }

  if (session && !sessionVerified) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(11,116,222,0.12),transparent_24%),linear-gradient(180deg,#f8fbff_0%,#eef4fb_100%)]" style={themeStyle}>
        <div className="flex flex-col items-center gap-4 text-slate-500">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[var(--lk-primary)]" />
          <span className="text-sm">Загрузка...</span>
        </div>
      </div>
    )
  }

  if (session) {
    if (!navigation.length) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(11,116,222,0.12),transparent_24%),linear-gradient(180deg,#f8fbff_0%,#eef4fb_100%)]" style={themeStyle}>
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
            <p className="text-sm font-medium text-slate-700">Для вашего аккаунта пока не выданы права на разделы ЛК сотрудника.</p>
          </div>
        </div>
      )
    }

    if (currentView === 'patients') {
      return (
        <StaffPatientsWorkspace
          bootstrap={bootstrap}
          navigation={navigation}
          currentView={currentView}
          onNavigate={navigateTo}
          session={session}
          onLogout={logout}
        />
      )
    }

    if (currentView === 'constructor') {
      return (
        <StaffConstructorWorkspace
          bootstrap={bootstrap}
          session={session}
          navigation={navigation}
          onNavigate={navigateTo}
          onLogout={logout}
        />
      )
    }

    if (currentView === 'plans') {
      return (
        <StaffPlansWorkspace
          bootstrap={bootstrap}
          navigation={navigation}
          currentView={currentView}
          onNavigate={navigateTo}
          session={session}
          onLogout={logout}
        />
      )
    }

    if (currentView === 'payment-links') {
      return (
        <StaffPaymentLinksWorkspace
          bootstrap={bootstrap}
          navigation={navigation}
          onNavigate={navigateTo}
          session={session}
          onLogout={logout}
        />
      )
    }

    return (
      <StaffOperationsWorkspace
        bootstrap={bootstrap}
        navigation={navigation}
          currentView={currentView as 'dashboard' | 'schedule' | 'communications' | 'users' | 'reports' | 'gift-certificates' | 'payments' | 'diagnocat' | 'promotions' | 'content' | 'analytics' | 'documents' | 'media'}
        onNavigate={navigateTo}
        session={session}
        onLogout={logout}
      />
    )
  }

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(11,116,222,0.12),transparent_24%),linear-gradient(180deg,#f8fbff_0%,#eef4fb_100%)] p-4 sm:p-6"
      style={themeStyle}
    >
      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[32px] border border-slate-200/80 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.12)] lg:grid-cols-[minmax(340px,0.92fr)_minmax(420px,1.08fr)]">
          <section
            className="relative hidden min-h-full flex-col justify-between overflow-hidden p-10 text-white lg:flex"
            style={{
              background: 'linear-gradient(155deg, color-mix(in srgb, var(--lk-primary) 86%, white) 0%, color-mix(in srgb, var(--lk-primary) 72%, black) 100%)',
            }}
          >
            <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at top left, rgba(255,255,255,0.45), transparent 38%)' }} />
            <div className="relative grid gap-8">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/14 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] backdrop-blur">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253 v13 m0 -13 C10.832 5.477 9.246 5 7.5 5 S4.168 5.477 3 6.253 v13 C4.168 18.477 5.754 18 7.5 18 s3.332 .477 4.5 1.253 m0 -13 C13.168 5.477 14.754 5 16.5 5 c1.746 0 3.332 .477 4.5 1.253 v13 C19.832 18.477 18.246 18 16.5 18 c-1.746 0 -3.332 .477 -4.5 1.253" />
                </svg>
              </div>
              <div className="grid gap-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/70">Личный кабинет клиники</p>
                <h1 className="text-4xl font-semibold leading-tight">{bootstrap.branding.appName}</h1>
                <p className="max-w-md text-sm leading-7 text-white/78">
                  Рабочий кабинет для врачей и сотрудников клиники. После входа здесь доступны расписание, пациенты, планы лечения и другие рабочие разделы.
                </p>
              </div>
            </div>

            <div className="relative grid gap-4 rounded-[28px] border border-white/16 bg-white/10 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] backdrop-blur">
              <div>
                <p className="text-sm font-semibold">Как войти</p>
                <p className="mt-2 text-sm leading-6 text-white/74">
                  Укажите номер телефона, который привязан к вашему аккаунту сотрудника. Мы отправим код подтверждения в доступный для этого номера канал.
                </p>
              </div>
              <ul className="grid gap-3 text-sm text-white/80">
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-white/90" />
                  <span>Если для номера доступно несколько каналов, вы сможете выбрать удобный вариант получения кода.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-white/90" />
                  <span>После авторизации откроются основные рабочие разделы кабинета: пациенты, расписание, планы лечения и сертификаты.</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="flex min-h-full items-center bg-white p-6 sm:p-8 lg:p-10">
            <div className="mx-auto w-full max-w-xl">
              <div className="mb-8 flex items-center gap-4 lg:hidden">
                <div
                  className="inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-[0_18px_34px_rgba(15,23,42,0.14)]"
                  style={{ background: 'linear-gradient(135deg, var(--lk-primary) 0%, color-mix(in srgb, var(--lk-primary) 78%, black) 100%)' }}
                >
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253 v13 m0 -13 C10.832 5.477 9.246 5 7.5 5 S4.168 5.477 3 6.253 v13 C4.168 18.477 5.754 18 7.5 18 s3.332 .477 4.5 1.253 m0 -13 C13.168 5.477 14.754 5 16.5 5 c1.746 0 3.332 .477 4.5 1.253 v13 C19.832 18.477 18.246 18 16.5 18 c-1.746 0 -3.332 .477 -4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">Личный кабинет клиники</p>
                  <h1 className="text-2xl font-semibold text-slate-900">{bootstrap.branding.appName}</h1>
                </div>
              </div>

              <div className="mb-8 grid gap-3">
                <span className="inline-flex w-fit items-center rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                  Для врачей и сотрудников
                </span>
                <div>
                  <h2 className="text-[clamp(28px,4vw,40px)] font-semibold leading-tight text-slate-900">
                    {authStep === 'phone' ? 'Вход в кабинет' : 'Подтвердите вход'}
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {authStep === 'phone'
                      ? 'Введите номер телефона, привязанный к вашему аккаунту, и получите код подтверждения.'
                      : 'Введите код подтверждения, который мы отправили на ваш номер телефона.'}
                  </p>
                </div>
              </div>

              {requestMessage ? (
                <div className={`mb-6 rounded-2xl border px-4 py-3 text-sm leading-6 ${noticeClassName(requestTone)}`}>
                  {requestMessage}
                </div>
              ) : null}

              {authStep === 'phone' ? (
                <div className="grid gap-6 rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.07)] sm:p-8">
                  <label className="grid gap-2">
                    <span className="text-sm font-medium text-slate-700">Номер телефона</span>
                    <input
                      className="w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      placeholder="+7 (___) ___-__-__"
                      autoComplete="tel"
                      inputMode="tel"
                    />
                    <span className="text-xs leading-5 text-slate-500">
                      Укажите номер, который привязан к вашему аккаунту врача или сотрудника.
                    </span>
                    {employeeFound === true && (
                      <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                        Сотрудник найден{employeeDisplayName ? `: ${employeeDisplayName}` : ''}
                      </span>
                    )}
                    {employeeFound === false && normalizePhone(phone).length >= 11 && (
                      <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                        Сотрудник с этим номером не найден
                      </span>
                    )}
                  </label>

                  <div className="grid gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-slate-700">Канал получения кода</span>
                      <span className="text-xs text-slate-400">
                        {channelsLoading ? 'Проверяем привязки…' : 'Появится после ввода номера телефона'}
                      </span>
                    </div>
                    {channelsError ? (
                      <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
                        {channelsError}
                      </div>
                    ) : null}
                    {!hasPhoneForChannels ? (
                      <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500">
                        Сначала введите номер телефона. Затем покажем доступные способы получения кода.
                      </div>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {authChannelOptions.map((option) => {
                          const selected = channel === option.channel
                          const selectable = option.selectable !== false

                          return (
                            <button
                              key={option.channel}
                              type="button"
                              className={`rounded-[20px] border px-4 py-4 text-left transition ${selected ? 'border-transparent text-white shadow-[0_14px_32px_rgba(15,23,42,0.12)]' : 'border-slate-200 bg-slate-50 text-slate-800 hover:border-sky-200 hover:bg-sky-50'} ${!selectable ? 'cursor-not-allowed opacity-60' : ''}`}
                              style={selected ? { background: 'linear-gradient(135deg, var(--lk-primary) 0%, color-mix(in srgb, var(--lk-primary) 78%, black) 100%)' } : undefined}
                              onClick={() => selectable && setChannel(option.channel)}
                              disabled={!selectable}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold">{option.displayName}</p>
                                  <p className={`mt-1 text-xs leading-5 ${selected ? 'text-white/80' : 'text-slate-500'}`}>
                                    {selectable ? 'Код подтверждения придет в этот канал.' : (option.blockedReason ?? 'Канал сейчас недоступен.')}
                                  </p>
                                  {!selectable && option.attachAction ? (
                                    <a
                                      className={`mt-2 inline-flex text-xs font-medium underline-offset-2 hover:underline ${selected ? 'text-white' : 'text-sky-700'}`}
                                      href={option.attachAction.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      {option.attachAction.label}
                                    </a>
                                  ) : null}
                                </div>
                                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${selected ? 'border-white/60 bg-white/15' : 'border-slate-300 bg-white'}`}>
                                  {selected ? <span className="h-2.5 w-2.5 rounded-full bg-white" /> : null}
                                </span>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {hasPhoneForChannels && authAttachActions.length ? (
                    <div className="grid gap-3 rounded-[22px] border border-sky-100 bg-sky-50/70 p-4">
                      <p className="text-sm font-medium text-slate-700">Дополнительные способы подключения</p>
                      <div className="flex flex-wrap gap-2.5">
                        {authAttachActions.map((action) => (
                          <a
                            key={`${action.channel}:${action.url}`}
                            className="inline-flex items-center justify-center rounded-[16px] border border-sky-200 bg-white px-4 py-2.5 text-sm font-medium text-sky-700 transition hover:bg-sky-100"
                            href={action.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {action.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <Button
                    type="button"
                    onClick={() => void requestCode()}
                    disabled={
                      loading
                      || channelsLoading
                      || employeeFound === false
                      || !authChannelOptions.some((option) => option.channel === channel && option.selectable !== false)
                    }
                  >
                    {loading ? 'Отправляем код...' : 'Получить код'}
                  </Button>
                </div>
              ) : (
                <div className="grid gap-6 rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.07)] sm:p-8">
                  <div className="grid gap-3 rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Код отправлен</p>
                        <p className="mt-1 text-sm text-slate-500">{requestedPhoneLabel ?? formatPhoneLabel(phone)}</p>
                      </div>
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                        {channelDisplayName(channel)}
                      </span>
                    </div>
                  </div>

                  <label className="grid gap-2">
                    <span className="text-sm font-medium text-slate-700">Код подтверждения</span>
                    <input
                      className="w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-center text-2xl tracking-[0.35em] text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                    />
                    <span className="text-xs leading-5 text-slate-500">
                      Если код не пришел, можно отправить его повторно или вернуться к выбору номера.
                    </span>
                  </label>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setAuthStep('phone')
                        setCode('')
                        setRequestTone('info')
                        setRequestMessage('Можно изменить номер телефона или запросить код заново.')
                      }}
                      disabled={loading}
                    >
                      Изменить номер
                    </Button>
                    <Button type="button" onClick={() => void verifyCode()} disabled={loading || code.length < 4}>
                      {loading ? 'Проверяем код...' : 'Войти'}
                    </Button>
                  </div>

                  <button
                    type="button"
                    className="w-fit text-sm font-medium text-sky-700 transition hover:text-sky-800 disabled:cursor-progress disabled:opacity-60"
                    onClick={() => void requestCode()}
                    disabled={loading}
                  >
                    Отправить код повторно
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
